<template>
    <form @submit.prevent="handleSubmit" class="admin-form">
      <div v-if="errorMessage" class="auth-error">
        <p class="mb-1 subtitle">{{ errorMessage }}</p>
      </div>

      <div class="form-group">
        <label for="name">Full Name</label>
        <input
          v-model="form.name"
          type="text"
          id="name"
          required
        />
      </div>

      <div class="form-group">
        <label for="email">Email</label>
        <input
          v-model="form.email"
          type="email"
          id="email"
          required
          :disabled="isEdit"
        />
      </div>

      <div v-if="!isEdit" class="info-message">
        <p class="info-text">
          <strong>Note:</strong> A temporary password will be generated automatically and a password reset email will be sent to the admin's email address.
        </p>
      </div>

      <div class="form-actions">
        <button type="button" class="btn btn-secondary" @click="$emit('cancel')">
          Cancel
        </button>
        <button type="submit" class="btn btn-primary" :disabled="isLoading">
          <loading-icon v-if="isLoading" color="white" size="16px" />
          {{ isEdit ? 'Update Admin' : 'Create Admin' }}
        </button>
      </div>
    </form>
  </template>

  <script setup>
  import { ref, computed, watch } from 'vue';
  import LoadingIcon from '@/components/loading.vue';

  const props = defineProps({
    admin: Object,
    isLoading: Boolean,
    errorMessage: String
  });

  const emit = defineEmits(['submit', 'cancel']);

  const isEdit = computed(() => !!props.admin);

  const form = ref({
    name: '',
    email: ''
  });

  watch(() => props.admin, (newAdmin) => {
    if (newAdmin) {
      form.value = {
        name: newAdmin.username || '',
        email: newAdmin.email || ''
      };
    }
  }, { immediate: true });

  const handleSubmit = () => {
    emit('submit', { ...form.value });
  };
  </script>

  <style scoped lang="scss">
  .admin-form {
    padding: 1rem;

    .auth-error {
      background-color: #f8d7da;
      border: 1px solid #f5c6cb;
      color: #721c24;
      padding: 0.75rem;
      border-radius: 4px;
      margin-bottom: 1rem;

      .subtitle {
        margin: 0;
        font-size: 0.9rem;
      }
    }

    .info-message {
      background-color: #d1ecf1;
      border: 1px solid #bee5eb;
      color: #0c5460;
      padding: 0.75rem;
      border-radius: 4px;
      margin-bottom: 1.5rem;

      .info-text {
        margin: 0;
        font-size: 0.9rem;
        line-height: 1.4;
      }
    }

    .form-group {
      margin-bottom: 1.5rem;

      label {
        display: block;
        margin-bottom: 0.5rem;
        font-weight: 600;
        color: #333;
        font-size: 0.9rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      input[type="text"],
      input[type="email"],
      input[type="password"] {
        width: 100%;
        padding: 0.75rem;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 1rem;
        transition: border-color 0.2s ease;

        &:focus {
          outline: none;
          border-color: var(--primary-color, #007bff);
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }

        &:disabled {
          background-color: #f8f9fa;
          cursor: not-allowed;
        }
      }
    }


    .form-actions {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
      padding-top: 1.5rem;
      border-top: 1px solid #e0e0e0;

      .btn {
        padding: 0.75rem 1.5rem;
        border-radius: 4px;
        border: none;
        cursor: pointer;
        font-weight: 500;
        transition: all 0.2s ease;

        &.btn-secondary {
          background-color: #6c757d;
          color: white;

          &:hover {
            background-color: #5a6268;
          }
        }

        &.btn-primary {
          background-color: var(--primary-color, #007bff);
          color: white;

          &:hover:not(:disabled) {
            background-color: var(--primary-color-dark, #0056b3);
          }

          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        }
      }
    }
  }
  </style>